import { TableInfo } from "api";
import {
  AlternateMenuItemOnClick,

  CallbackOrBoolean,
  CallbackOrString,

  CrudOptions,
  applyMaybeBoolean,
  applyMaybeString,
  checkDisableMenuItemBasedOnSelectedDataValue,
} from "components/Category/CrudMenu";
import { TableOptionItem } from "components/RowOptions";
import { RightMenu } from "components/rightMenu/RightMenu";
import { RightPanel } from "components/rightPanel/RightPanel";

import { useHandleParentActionFromStorage } from "helpers/useHandleParentActionFromStorage";
import { DisableMenuItemData } from "pages/PlatformUsers/PlatformUsers";
import React, { ReactElement, ReactNode, ReactText, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectRedirectUrl } from "reducers/listPage";

export type Action =
  | {
      type: "change_open_menu";
      payload: {
        parentCrudOptions: ParentCrudOptions;
        selectedId: React.ReactText | null;
        fromRowOption: boolean;
      };
    }
  | { type: "close_menus" };

type State = {
  currentMenu: ParentCrudOptions;
  selectedId: React.ReactText | null;
  fromRowOption: boolean;
};

const initialState: State = {
  currentMenu: null,
  selectedId: null,
  fromRowOption: false,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "change_open_menu":
      return {
        ...state,
        currentMenu: action.payload.parentCrudOptions,
        selectedId: action.payload.selectedId,
        fromRowOption: action.payload.fromRowOption,
      };
    case "close_menus":
      return {
        ...state,
        currentMenu: null,
        selectedId: null,
        fromRowOption: false,
      };
  }
}
type Menu = {
  currentMenu: ParentCrudOptions;
  menuItems: ParentCrudMenuItems;
  selectedId: React.ReactText | null;
  selectedData: any;
  fromRowOption: boolean;
};

type parentCrudActions = {
  openMenu(
    menu: ParentCrudOptions,
    id: React.ReactText | null,
    fromRowOption?: boolean
  ): void;
  closeMenus(): void;
}
type openMenu = ( menu: ParentCrudOptions,
  id: React.ReactText | null,
  fromRowOption?: boolean) =>void


export type ParentCrudMenuState = [
  Menu,
parentCrudActions,
CardActions
];

export type AlternateParentCrudMenuItems = {
  menuItems: ParentCrudMenuItem[];
};

export type ParentCrudMenuItem = {
  icon: string;
  name: string;
  imgAlt: string;
  disabled?: boolean;
  onClick(id?: React.ReactText | null): void;
  permissionKey:string|undefined;
};

type ParentCrudMenuItems = {
  menuItems: ParentCrudMenuItem[];
  alternateMenuItems: AlternateParentCrudMenuItems;
};

interface UseParentCrudMenuStateParams<T> {
  menuItems: ParentCrudItems<T> | null;
  selectedData: T[];
  permissions: Map<string, boolean>;
  table?: TableInfo;
  idColumn?: string;
  disableMenuItemData?: DisableMenuItemData; // Field names and values that can also decides if a menu item is disabled or not
}
const ParentCrudOptions: ParentCrudOptions[] = [
  "VIEW",
  "DEACTIVATE",
  "DELETE",
  "DOCS",
  "CONTACTS",
  "GATEWAYS",
  "ASSETS",
  "SURVEYS",
  "SNAG",
  "DESIGN_REQ",
  "MSG",
  "HISTORY",
  "WORKFLOWS",
];
export type CardActions = {
  [key in NonNullable<ParentCrudOptions> as Lowercase<key>]: (id:number,allData:any) => void;
};

export function useParentCrudMenuState<Data>(
  props: UseParentCrudMenuStateParams<Data>
) {
  const {
    menuItems,
    selectedData: rawSelectedData,
    permissions,
    table,
    idColumn,
    disableMenuItemData,
  } = props;
  if (menuItems) {
   
    const [store, dispatch] = React.useReducer(reducer, initialState);
    const { currentMenu, selectedId, fromRowOption } = store;
    const redirect = useSelector(selectRedirectUrl)
    const getSelectedData = (table: TableInfo): any => {
      if (selectedId && idColumn) {
        if(redirect){
          const found = table.data?.details.listData.find(
            (data: any) => data['projects__ID'] === Number(selectedId)
          );
          return found ? [found] : [];
        }
        const found = table.data?.details.listData.find(
          (data: any) => data[idColumn] === selectedId
        );
        return found ? [found] : [];
      }

      return [];
    };
    const selectedData =
      fromRowOption && table ? getSelectedData(table) : rawSelectedData;
    
    useHandleParentActionFromStorage(
    menuItems ,
    selectedId,
    fromRowOption,
    dispatch ,
    );
    
    const openMenu = React.useCallback(
      (
        menu: ParentCrudOptions,
        selectedId: React.ReactText | null,
        fromRowOption?: boolean
      ) =>
        dispatch({
          type: "change_open_menu",
          payload: {
            parentCrudOptions: menu,
            selectedId: selectedId,
            fromRowOption: fromRowOption ?? false,
          },
        }),
      [dispatch]
    );
    const closeMenus = React.useCallback(
      () => dispatch({ type: "close_menus" }),
      [dispatch]
    );

    const generateCardViewActions = (openMenu:openMenu,selectedId:ReactText|null): CardActions => {
      const parentCardActions: Partial<CardActions> = {};
    
      ParentCrudOptions.forEach((option) => {
        if (option !== null) {
          parentCardActions[option.toLowerCase() as keyof CardActions] = (id,allData) => openMenu(`${option}`,id,allData);
        }
      });
    
      return parentCardActions as CardActions;
    };

    const cardActions = generateCardViewActions(openMenu,selectedId)

    const generateMenuItems = (
      menuItems: ParentCrudItems<Data>
    ): ParentCrudMenuItems => {
      const singleItems: ParentCrudMenuItem[] = menuItems.map(item => {
        const hasPermission =
          item.permissionsKey == null
            ? true
            : !!permissions.get(item.permissionsKey);
        const disableMenuItemBasedOnSelectedDataValue = checkDisableMenuItemBasedOnSelectedDataValue(
          item.permissionsKey,
          disableMenuItemData,
          selectedData
        );
        const isDisabled =
          !hasPermission || disableMenuItemBasedOnSelectedDataValue;

        const menuItemOnClick = isDisabled
          ? () => {}
          : () => openMenu(item.menuCode, null);

        return {
          icon: applyMaybeString(item.icon, selectedData),
          name: applyMaybeString(item.title, selectedData),
          imgAlt: applyMaybeString(item.imgAltText, selectedData),
          disabled: isDisabled,
          onClick: menuItemOnClick,
          permissionKey:item.permissionsKey
        };
      });
      const multiItems: AlternateParentCrudMenuItems = {
        menuItems: menuItems
          .filter(x => applyMaybeBoolean(x.showInMultiSelect, selectedData))
          .map(item => {
            const hasPermission =
              item.permissionsKey == null
                ? true
                : !!permissions.get(item.permissionsKey);
            const isDisabled = !hasPermission;
            return {
              icon: applyMaybeString(item.icon, selectedData),
              name: applyMaybeString(item.title, selectedData),
              imgAlt: applyMaybeString(item.imgAltText, selectedData),
              onClick: isDisabled
                ? () => {}
                : () => openMenu(item.menuCode, null),
              disabled: isDisabled,
              permissionKey:item.permissionsKey
            };
          }),
      };

      return { menuItems: singleItems, alternateMenuItems: multiItems };
    };
    const finalMenuItems: ParentCrudMenuItems = generateMenuItems(menuItems);

    const output = React.useMemo(() => {
      const data = {
        currentMenu,
        menuItems: finalMenuItems,
        selectedId,
        selectedData,
        fromRowOption,
      };
      const actions = { openMenu, closeMenus };
      return [data, actions, cardActions] as ParentCrudMenuState;
    }, [
      openMenu,
      closeMenus,
      currentMenu,
      finalMenuItems,
      selectedId,
      fromRowOption,
    ]);

    return output;
  } else {
    return [];
  }
}

export type ParentCrudOptions =
  | "VIEW"
  | "UPDATE"
  | "DEACTIVATE"
  | "DELETE"
  | "DOCS"
  | "CONTACTS"
  | "ASSETS"
  | "GATEWAYS"
  | "SURVEYS"
  | "SNAG"
  | "DESIGN_REQ"
  | "MSG"
  | "HISTORY"
  | "FILTERS"
  | "WORKFLOWS"
  | "VIEWSITE"
  | "KPI"
  | "HISTORY"
  | null;

export type ParentCrudItems<Data, Options = ParentCrudOptions> = {
  icon: CallbackOrString<Data>;
  imgAltText: CallbackOrString<Data>;
  menuCode: Options;
  showInMultiSelect: CallbackOrBoolean<Data>;
  permissionsKey?: string;
  title: CallbackOrString<Data>;
  notInMenus?: boolean;
  inRightPanel: CallbackOrBoolean<Data>;
  isDisabled?:boolean,
  panelHeader?: (selectedItems: Data) => React.ReactNode;
  alternateMenuItemOnClick?: AlternateMenuItemOnClick;
  content(
    onDone: () => void,
    selectedData: Data[],
    menu: Menu,
    additionalData?: any,clickedIdIfMultiIds?: string | number
  ): React.ReactNode;
  customItem?: (
    props?: (Omit<ParentCrudMenuItem, "customItem"> | TableOptionItem) & {
      additionalDataIDs?: any;
    }
  ) => ReactElement;
}[];

function ParentCrudMenu<Data>(props: {
  menu: Menu;
  closePanel(): void;
  onDone(): void;
  onRightMenuClose: () => void;
  selectedData: Data[];
  parentCrudItems: ParentCrudItems<Data>;
  menuItems: ParentCrudMenuItems;
  largeRightPanel?: boolean | ((action?: string | null) => boolean);
  idColumn: string;
  filters: React.ReactNode;
  filtersHeaderElement?: React.ReactElement;
  additionalData?: any;
    menuActionsCode?: CrudOptions;
  autoOpenPanel?: (val?: any) => void;
  clickedIdIfMultiIds?: string | number;
}) {
  const {
    closePanel,
    onDone,
    onRightMenuClose,
    menu,
    parentCrudItems,
    selectedData,
    filtersHeaderElement,
    filters,
    largeRightPanel,
    idColumn,
    additionalData,menuActionsCode, autoOpenPanel, clickedIdIfMultiIds
  } = props;

  const { menuItems, currentMenu } = menu;

  const currentItem = !currentMenu
    ? null
    : parentCrudItems.find(
        item => item.menuCode?.toLowerCase() === currentMenu?.toLowerCase()
      );

  const title = () => {
    if (!currentMenu) {
      return "";
    } else if (currentMenu === "FILTERS") {
      return "Filters";
    } else if (!currentItem) {
      return "";
    } else if (currentItem.panelHeader) {
      let selectedRow = menu.selectedData[0];
      if (clickedIdIfMultiIds) { selectedRow = menu.selectedData?.find((d: any) => String(d[idColumn]) === String(clickedIdIfMultiIds)) }
      return currentItem.panelHeader(selectedRow);
    }
    return applyMaybeString(currentItem.title, selectedData);
  };

  const content = () => {
    if (currentMenu === "FILTERS") return filters;
    else if (!currentMenu || !currentItem) {
      return null;
    }
    if (parentCrudItems) {
      return currentItem.content(onDone, selectedData, menu, additionalData, clickedIdIfMultiIds);
    }
  };

  const headerElement =
    currentMenu === "FILTERS" ? filtersHeaderElement : undefined;

    const checkShouldUseLargePanel = () => {
      if (largeRightPanel === undefined) return false;
      if (currentMenu === "FILTERS") return false;
      if (typeof largeRightPanel === "boolean") return largeRightPanel;
      else return largeRightPanel(currentItem?.menuCode);
    };
  
    const shouldUseLargePanel = checkShouldUseLargePanel();

  const selectedDataIDs: number[] | null =
    selectedData &&
    Array.isArray(selectedData) &&
    selectedData.length > 0 &&
    selectedData[0] &&
    typeof selectedData[0] === "object"
      ? selectedData
          .map((item: any) => {
            if (item[idColumn as keyof typeof item]) {
              return item[idColumn as keyof typeof item];
            } else {
              return null;
            }
          })
          .filter(item => item !== null)
      : null;

  let rightPanelOpen =
    parentCrudItems && currentMenu
      ? applyMaybeBoolean(currentItem?.inRightPanel ?? true, selectedData)
      : false;
  
  useEffect(() => {
    if (menuActionsCode) {
      if (rightPanelOpen) closePanel();
      autoOpenPanel && autoOpenPanel(menuActionsCode);

    }
  }, [menuActionsCode]);

  
  return (
    <React.Fragment>
      {(currentItem || currentMenu === "FILTERS") && (
        <RightPanel
          title={title()}
          rightPanelContent={rightPanelOpen}
          closePanel={closePanel}
          headerElement={headerElement}
          largeRightPanel={shouldUseLargePanel}
          extraClassNames="ParentCrudRightPanel"
        >
          {content()}
        </RightPanel>
      )}
      {!rightPanelOpen && content()}
      <RightMenu
        toggleAltMenuItems={selectedData.length > 1}
        visible={!menu.fromRowOption && selectedData.length > 0}
        menuItems={menuItems.menuItems}
        alternateMenuItems={menuItems.alternateMenuItems}
        selectedDataIDs={selectedDataIDs}
        additionalDataIDs={additionalData}
        onRightMenuClose={onRightMenuClose}
      />
    </React.Fragment>
  );
}
export default ParentCrudMenu;
